import type { IconType } from "react-icons";
import {
  MdAccountBalance,
  MdAgriculture,
  MdArchitecture,
  MdAutoAwesome,
  MdBiotech,
  MdBrush,
  MdBusinessCenter,
  MdCalculate,
  MdCameraAlt,
  MdChildCare,
  MdCode,
  MdComputer,
  MdElectricBolt,
  MdEngineering,
  MdFlag,
  MdFlight,
  MdGavel,
  MdHealthAndSafety,
  MdHistoryEdu,
  MdHome,
  MdHotel,
  MdLanguage,
  MdLibraryBooks,
  MdLocalHospital,
  MdLocalPharmacy,
  MdMap,
  MdMemory,
  MdMenuBook,
  MdMic,
  MdMovie,
  MdMusicNote,
  MdNewspaper,
  MdPeople,
  MdPrecisionManufacturing,
  MdPsychology,
  MdPublic,
  MdQueryStats,
  MdRestaurant,
  MdSchool,
  MdScience,
  MdSecurity,
  MdSelfImprovement,
  MdShowChart,
  MdSportsSoccer,
  MdStorefront,
  MdTerrain,
  MdTranslate,
  MdTravelExplore,
  MdVolunteerActivism,
} from "react-icons/md";
import { FaBalanceScale, FaQuran, FaTheaterMasks, FaRobot, FaTooth } from "react-icons/fa";
import { GiAtom, GiPlantSeed, GiPlanetConquest } from "react-icons/gi";
import { BiMath } from "react-icons/bi";
import { HiOutlineLightBulb } from "react-icons/hi";
import { IoEarthOutline, IoFlaskOutline } from "react-icons/io5";
import { TbGeometry, TbBrain } from "react-icons/tb";

/**
 * Curated subject icon catalog for admin forms.
 * Keys must stay in sync with:
 * - back/config/subject_icons.php
 * - app/constants/subjectIcons.ts
 */
export type SubjectIconKey =
  | "general"
  | "math"
  | "geometry"
  | "algebra"
  | "statistics"
  | "physics"
  | "chemistry"
  | "biology"
  | "science"
  | "anatomy"
  | "zoology"
  | "botany"
  | "earth_science"
  | "geology"
  | "astronomy"
  | "environment"
  | "arabic"
  | "english"
  | "french"
  | "german"
  | "spanish"
  | "italian"
  | "chinese"
  | "japanese"
  | "russian"
  | "turkish"
  | "portuguese"
  | "language"
  | "literature"
  | "history"
  | "geography"
  | "social_studies"
  | "civics"
  | "religion"
  | "islamic"
  | "quran"
  | "philosophy"
  | "psychology"
  | "sociology"
  | "political_science"
  | "ethics"
  | "logic"
  | "law"
  | "economics"
  | "accounting"
  | "business"
  | "finance"
  | "marketing"
  | "entrepreneurship"
  | "computer"
  | "technology"
  | "coding"
  | "data_science"
  | "ai"
  | "cybersecurity"
  | "electronics"
  | "mechanics"
  | "robotics"
  | "engineering"
  | "architecture"
  | "design"
  | "art"
  | "drawing"
  | "music"
  | "drama"
  | "dance"
  | "film"
  | "photography"
  | "media"
  | "journalism"
  | "sports"
  | "health"
  | "medicine"
  | "nursing"
  | "pharmacy"
  | "dentistry"
  | "agriculture"
  | "home_economics"
  | "cooking"
  | "fashion"
  | "tourism"
  | "hospitality"
  | "aviation"
  | "kindergarten"
  | "life_skills"
  | "library"
  | "research"
  | "calligraphy";

export type SubjectIconOption = {
  value: SubjectIconKey;
  labelAr: string;
  labelEn: string;
  accent: string;
  Icon: IconType;
};

export const SUBJECT_ICON_DEFAULT: SubjectIconKey = "general";

export const SUBJECT_ICON_OPTIONS: SubjectIconOption[] = [
  { value: "general", labelAr: "عام", labelEn: "General", accent: "#3D8BFF", Icon: MdSchool },
  { value: "math", labelAr: "رياضيات", labelEn: "Mathematics", accent: "#E8A838", Icon: MdCalculate },
  { value: "geometry", labelAr: "هندسة رياضية", labelEn: "Geometry", accent: "#D4A017", Icon: TbGeometry },
  { value: "algebra", labelAr: "جبر", labelEn: "Algebra", accent: "#E8A838", Icon: BiMath },
  { value: "statistics", labelAr: "إحصاء", labelEn: "Statistics", accent: "#E8A838", Icon: MdQueryStats },
  { value: "physics", labelAr: "فيزياء", labelEn: "Physics", accent: "#5B8DEF", Icon: GiAtom },
  { value: "chemistry", labelAr: "كيمياء", labelEn: "Chemistry", accent: "#E4572E", Icon: IoFlaskOutline },
  { value: "biology", labelAr: "أحياء", labelEn: "Biology", accent: "#2BB673", Icon: GiPlantSeed },
  { value: "science", labelAr: "علوم", labelEn: "Science", accent: "#00A8A8", Icon: MdScience },
  { value: "anatomy", labelAr: "تشريح", labelEn: "Anatomy", accent: "#E4572E", Icon: MdBiotech },
  { value: "zoology", labelAr: "علم الحيوان", labelEn: "Zoology", accent: "#2BB673", Icon: MdBiotech },
  { value: "botany", labelAr: "علم النبات", labelEn: "Botany", accent: "#2FA46F", Icon: GiPlantSeed },
  { value: "earth_science", labelAr: "علوم الأرض", labelEn: "Earth science", accent: "#00A8A8", Icon: IoEarthOutline },
  { value: "geology", labelAr: "جيولوجيا", labelEn: "Geology", accent: "#B86B4A", Icon: MdTerrain },
  { value: "astronomy", labelAr: "فلك", labelEn: "Astronomy", accent: "#5B7CFF", Icon: GiPlanetConquest },
  { value: "environment", labelAr: "بيئة", labelEn: "Environment", accent: "#00A8A8", Icon: MdScience },
  { value: "arabic", labelAr: "لغة عربية", labelEn: "Arabic", accent: "#C17A3A", Icon: MdMenuBook },
  { value: "english", labelAr: "لغة إنجليزية", labelEn: "English", accent: "#3D8BFF", Icon: MdTranslate },
  { value: "french", labelAr: "لغة فرنسية", labelEn: "French", accent: "#4C6EF5", Icon: MdLanguage },
  { value: "german", labelAr: "لغة ألمانية", labelEn: "German", accent: "#D4A017", Icon: MdPublic },
  { value: "spanish", labelAr: "لغة إسبانية", labelEn: "Spanish", accent: "#E4572E", Icon: MdLanguage },
  { value: "italian", labelAr: "لغة إيطالية", labelEn: "Italian", accent: "#2BB673", Icon: MdLanguage },
  { value: "chinese", labelAr: "لغة صينية", labelEn: "Chinese", accent: "#E4572E", Icon: MdTranslate },
  { value: "japanese", labelAr: "لغة يابانية", labelEn: "Japanese", accent: "#E4572E", Icon: MdTranslate },
  { value: "russian", labelAr: "لغة روسية", labelEn: "Russian", accent: "#5B8DEF", Icon: MdLanguage },
  { value: "turkish", labelAr: "لغة تركية", labelEn: "Turkish", accent: "#E4572E", Icon: MdLanguage },
  { value: "portuguese", labelAr: "لغة برتغالية", labelEn: "Portuguese", accent: "#2BB673", Icon: MdLanguage },
  { value: "language", labelAr: "لغات", labelEn: "Languages", accent: "#5B7CFF", Icon: MdLanguage },
  { value: "literature", labelAr: "أدب", labelEn: "Literature", accent: "#C17A3A", Icon: MdLibraryBooks },
  { value: "history", labelAr: "تاريخ", labelEn: "History", accent: "#B86B4A", Icon: MdHistoryEdu },
  { value: "geography", labelAr: "جغرافيا", labelEn: "Geography", accent: "#00A8A8", Icon: MdMap },
  { value: "social_studies", labelAr: "دراسات اجتماعية", labelEn: "Social studies", accent: "#8B6B4A", Icon: MdPeople },
  { value: "civics", labelAr: "تربية وطنية", labelEn: "Civics", accent: "#3D8BFF", Icon: MdFlag },
  { value: "religion", labelAr: "تربية دينية", labelEn: "Religion", accent: "#2FA46F", Icon: MdSelfImprovement },
  { value: "islamic", labelAr: "تربية إسلامية", labelEn: "Islamic studies", accent: "#2FA46F", Icon: MdVolunteerActivism },
  { value: "quran", labelAr: "قرآن كريم", labelEn: "Quran", accent: "#2FA46F", Icon: FaQuran },
  { value: "philosophy", labelAr: "فلسفة", labelEn: "Philosophy", accent: "#5B7CFF", Icon: HiOutlineLightBulb },
  { value: "psychology", labelAr: "علم نفس", labelEn: "Psychology", accent: "#9B59B6", Icon: MdPsychology },
  { value: "sociology", labelAr: "علم اجتماع", labelEn: "Sociology", accent: "#8B6B4A", Icon: MdPeople },
  { value: "political_science", labelAr: "علوم سياسية", labelEn: "Political science", accent: "#3D8BFF", Icon: MdAccountBalance },
  { value: "ethics", labelAr: "أخلاق", labelEn: "Ethics", accent: "#2FA46F", Icon: FaBalanceScale },
  { value: "logic", labelAr: "منطق", labelEn: "Logic", accent: "#5B7CFF", Icon: TbBrain },
  { value: "law", labelAr: "قانون", labelEn: "Law", accent: "#4A5170", Icon: MdGavel },
  { value: "economics", labelAr: "اقتصاد", labelEn: "Economics", accent: "#2BB673", Icon: MdShowChart },
  { value: "accounting", labelAr: "محاسبة", labelEn: "Accounting", accent: "#E8A838", Icon: MdQueryStats },
  { value: "business", labelAr: "إدارة أعمال", labelEn: "Business", accent: "#3D8BFF", Icon: MdBusinessCenter },
  { value: "finance", labelAr: "مالية", labelEn: "Finance", accent: "#2BB673", Icon: MdAccountBalance },
  { value: "marketing", labelAr: "تسويق", labelEn: "Marketing", accent: "#E4572E", Icon: MdStorefront },
  { value: "entrepreneurship", labelAr: "ريادة أعمال", labelEn: "Entrepreneurship", accent: "#E8A838", Icon: MdAutoAwesome },
  { value: "computer", labelAr: "حاسب آلي", labelEn: "Computer", accent: "#3D8BFF", Icon: MdComputer },
  { value: "technology", labelAr: "تكنولوجيا", labelEn: "Technology", accent: "#5B7CFF", Icon: MdMemory },
  { value: "coding", labelAr: "برمجة", labelEn: "Coding", accent: "#00A8A8", Icon: MdCode },
  { value: "data_science", labelAr: "علوم بيانات", labelEn: "Data science", accent: "#5B8DEF", Icon: MdQueryStats },
  { value: "ai", labelAr: "ذكاء اصطناعي", labelEn: "Artificial intelligence", accent: "#5B7CFF", Icon: MdAutoAwesome },
  { value: "cybersecurity", labelAr: "أمن سيبراني", labelEn: "Cybersecurity", accent: "#4A5170", Icon: MdSecurity },
  { value: "electronics", labelAr: "إلكترونيات", labelEn: "Electronics", accent: "#E8A838", Icon: MdElectricBolt },
  { value: "mechanics", labelAr: "ميكانيكا", labelEn: "Mechanics", accent: "#B86B4A", Icon: MdPrecisionManufacturing },
  { value: "robotics", labelAr: "روبوتات", labelEn: "Robotics", accent: "#5B7CFF", Icon: FaRobot },
  { value: "engineering", labelAr: "هندسة", labelEn: "Engineering", accent: "#3D8BFF", Icon: MdEngineering },
  { value: "architecture", labelAr: "عمارة", labelEn: "Architecture", accent: "#C17A3A", Icon: MdArchitecture },
  { value: "design", labelAr: "تصميم", labelEn: "Design", accent: "#E4572E", Icon: MdBrush },
  { value: "art", labelAr: "فنون", labelEn: "Art", accent: "#E4572E", Icon: MdBrush },
  { value: "drawing", labelAr: "رسم", labelEn: "Drawing", accent: "#C17A3A", Icon: MdBrush },
  { value: "music", labelAr: "موسيقى", labelEn: "Music", accent: "#5B7CFF", Icon: MdMusicNote },
  { value: "drama", labelAr: "مسرح", labelEn: "Drama", accent: "#E8A838", Icon: FaTheaterMasks },
  { value: "dance", labelAr: "رقص", labelEn: "Dance", accent: "#E4572E", Icon: MdSelfImprovement },
  { value: "film", labelAr: "سينما", labelEn: "Film", accent: "#5B8DEF", Icon: MdMovie },
  { value: "photography", labelAr: "تصوير", labelEn: "Photography", accent: "#4A5170", Icon: MdCameraAlt },
  { value: "media", labelAr: "إعلام", labelEn: "Media", accent: "#5B8DEF", Icon: MdMic },
  { value: "journalism", labelAr: "صحافة", labelEn: "Journalism", accent: "#3D8BFF", Icon: MdNewspaper },
  { value: "sports", labelAr: "رياضة", labelEn: "Sports", accent: "#2BB673", Icon: MdSportsSoccer },
  { value: "health", labelAr: "صحة", labelEn: "Health", accent: "#E4572E", Icon: MdHealthAndSafety },
  { value: "medicine", labelAr: "طب", labelEn: "Medicine", accent: "#E4572E", Icon: MdLocalHospital },
  { value: "nursing", labelAr: "تمريض", labelEn: "Nursing", accent: "#00A8A8", Icon: MdHealthAndSafety },
  { value: "pharmacy", labelAr: "صيدلة", labelEn: "Pharmacy", accent: "#2BB673", Icon: MdLocalPharmacy },
  { value: "dentistry", labelAr: "طب أسنان", labelEn: "Dentistry", accent: "#5B8DEF", Icon: FaTooth },
  { value: "agriculture", labelAr: "زراعة", labelEn: "Agriculture", accent: "#2BB673", Icon: MdAgriculture },
  { value: "home_economics", labelAr: "اقتصاد منزلي", labelEn: "Home economics", accent: "#E4572E", Icon: MdHome },
  { value: "cooking", labelAr: "طبخ", labelEn: "Cooking", accent: "#E8A838", Icon: MdRestaurant },
  { value: "fashion", labelAr: "أزياء", labelEn: "Fashion", accent: "#E4572E", Icon: MdBrush },
  { value: "tourism", labelAr: "سياحة", labelEn: "Tourism", accent: "#00A8A8", Icon: MdTravelExplore },
  { value: "hospitality", labelAr: "ضيافة", labelEn: "Hospitality", accent: "#C17A3A", Icon: MdHotel },
  { value: "aviation", labelAr: "طيران", labelEn: "Aviation", accent: "#3D8BFF", Icon: MdFlight },
  { value: "kindergarten", labelAr: "رياض أطفال", labelEn: "Kindergarten", accent: "#E8A838", Icon: MdChildCare },
  { value: "life_skills", labelAr: "مهارات حياتية", labelEn: "Life skills", accent: "#2BB673", Icon: MdSelfImprovement },
  { value: "library", labelAr: "مكتبة", labelEn: "Library", accent: "#C17A3A", Icon: MdLibraryBooks },
  { value: "research", labelAr: "بحث علمي", labelEn: "Research", accent: "#5B7CFF", Icon: MdScience },
  { value: "calligraphy", labelAr: "خط عربي", labelEn: "Calligraphy", accent: "#C17A3A", Icon: MdBrush },
];

export function getSubjectIconOption(key: string | null | undefined): SubjectIconOption {
  return (
    SUBJECT_ICON_OPTIONS.find((option) => option.value === key) ??
    SUBJECT_ICON_OPTIONS.find((option) => option.value === SUBJECT_ICON_DEFAULT)!
  );
}

export function subjectIconSelectOptions(isArabic: boolean) {
  return SUBJECT_ICON_OPTIONS.map((option) => ({
    value: option.value,
    label: isArabic ? option.labelAr : option.labelEn,
  }));
}
